import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  getStoredConsent,
  saveConsent,
  CONSENT_REOPEN_EVENT,
} from '../lib/cookieConsent';
import { initAnalyticsIfConsented, disableAnalytics } from '../lib/analytics';

export const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analyticsDraft, setAnalyticsDraft] = useState(false);

  useEffect(() => {
    const consent = getStoredConsent();
    if (!consent) {
      // Sem decisão salva ainda — banner aparece, nada de analytics roda.
      setVisible(true);
    } else {
      setAnalyticsDraft(consent.analytics);
      initAnalyticsIfConsented(consent.analytics);
    }

    // Reabre o painel (em modo "personalizar") quando o link "Preferências
    // de cookies" do rodapé é clicado, mesmo depois de o usuário já ter
    // decidido antes.
    const handleReopen = () => {
      const current = getStoredConsent();
      setAnalyticsDraft(current?.analytics ?? false);
      setExpanded(true);
      setVisible(true);
    };
    window.addEventListener(CONSENT_REOPEN_EVENT, handleReopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, handleReopen);
  }, []);

  const applyAndClose = (analytics) => {
    saveConsent(analytics);
    if (analytics) {
      initAnalyticsIfConsented(true);
    } else {
      disableAnalytics();
    }
    setVisible(false);
    setExpanded(false);
  };

  const handleAcceptAll = () => applyAndClose(true);
  const handleRejectNonEssential = () => applyAndClose(false);
  const handleSavePreferences = () => applyAndClose(analyticsDraft);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-card shadow-[0_-4px_24px_rgba(0,0,0,0.08)]"
      role="dialog"
      aria-live="polite"
      aria-label="Preferências de cookies"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-5xl mx-auto px-4 py-4 md:px-6 md:py-5 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Cookie className="w-5 h-5 mt-0.5 text-orange-500 shrink-0" />
            <p className="text-sm text-muted-foreground leading-relaxed">
              Usamos cookies para o site funcionar e, com sua permissão, para entender
              como ele é usado e medir campanhas. Você pode aceitar todos os cookies ou
              recusar os não essenciais a qualquer momento.{' '}
              <Link to="/privacy#cookies" className="text-orange-600 dark:text-orange-400 underline underline-offset-2 hover:no-underline">
                Saiba mais
              </Link>
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => setExpanded((v) => !v)}
              data-testid="cookie-customize-toggle"
            >
              Personalizar
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRejectNonEssential}
              data-testid="cookie-reject"
            >
              Recusar não essenciais
            </Button>
            <Button
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={handleAcceptAll}
              data-testid="cookie-accept-all"
            >
              Aceitar todos
            </Button>
          </div>
        </div>

        {expanded && (
          <div className="border-t border-border pt-4 space-y-3" data-testid="cookie-preferences-panel">
            <div className="flex items-center justify-between gap-4 rounded-lg bg-muted/50 p-3">
              <div>
                <p className="text-sm font-medium">Necessários</p>
                <p className="text-xs text-muted-foreground">
                  Essenciais para o site funcionar (ex: login, segurança). Não podem ser desativados.
                </p>
              </div>
              <Switch checked disabled aria-label="Cookies necessários, sempre ativos" />
            </div>

            <div className="flex items-center justify-between gap-4 rounded-lg bg-muted/50 p-3">
              <div>
                <p className="text-sm font-medium">Analytics e Marketing</p>
                <p className="text-xs text-muted-foreground">
                  Usados para entender como o site é usado e medir o desempenho de campanhas.
                </p>
              </div>
              <Switch
                checked={analyticsDraft}
                onCheckedChange={setAnalyticsDraft}
                aria-label="Cookies de analytics e marketing"
                data-testid="cookie-analytics-switch"
              />
            </div>

            <div className="flex justify-end">
              <Button
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                onClick={handleSavePreferences}
                data-testid="cookie-save-preferences"
              >
                Salvar preferências
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
